<template>
  <div>
    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" label="Loading..." style="width: 3rem; height: 3rem;"></b-spinner>
      <p class="mt-3 h5">Loading stakeholder information...</p>
    </div>

    <!-- Error State -->
    <b-alert v-else-if="error" show variant="danger" class="mt-3">
      <h4>Error Loading Stakeholder</h4>
      <p>{{ error }}</p>
      <b-button @click="fetchStakeholder" variant="outline-danger">Try Again</b-button>
    </b-alert>

    <!-- Main Content - Only show when data is loaded -->
    <div v-else>
      <!-- Stakeholder Info -->
      <b-card>
        <div style="background-color: rgb(97, 116, 152); height: 32px; border-radius: 4px;">
          <h4 style="color: rgb(223, 227, 238); margin-left: 5px; font-weight: bold;">
            Stakeholder Information
          </h4>
        </div>

        <b-row>
          <!-- Name -->
          <b-col md="4" cols="12">
            <b-form-group label="Name:">
              <b-form-input v-model="stakeholder.name" disabled />
            </b-form-group>
          </b-col>

          <!-- Email -->
          <b-col md="4" cols="12">
            <b-form-group label="Email:">
              <b-form-input v-model="stakeholder.email" disabled />
            </b-form-group>
          </b-col>

          <!-- Phone -->
          <b-col md="4" cols="12">
            <b-form-group label="Phone Number:">
              <b-form-input v-model="stakeholder.phone" disabled />
            </b-form-group>
          </b-col>

          <!-- Address -->
          <b-col md="4" cols="12">
            <b-form-group label="Address:">
              <b-form-input v-model="stakeholder.address" disabled />
            </b-form-group>
          </b-col>

          <!-- Type -->
          <b-col md="4" cols="12">
            <b-form-group label="Type:">
              <b-form-input v-model="stakeholder.type" disabled />
            </b-form-group>
          </b-col>

          <!-- Share Percentage -->
          <b-col md="4" cols="12">
            <b-form-group label="Share Percentage (%):">
              <b-form-input v-model="stakeholder.share_percentage" disabled />
            </b-form-group>
          </b-col>

          <!-- Profile Picture -->
          <b-col md="4" cols="12">
            <b-form-group label="Profile Picture:">
              <div style="margin-left: 3px; margin-bottom: 15px">
                <div v-if="stakeholder.image">
                  <img
                    :src="fullUrl(stakeholder.image)"
                    alt="Profile Picture"
                    width="100"
                    height="100"
                    style="cursor: pointer"
                    @click="openLightbox(fullUrl(stakeholder.image))"
                  />
                </div>
                <div v-else>
                  <span>No image available</span>
                </div>
              </div>
            </b-form-group>
          </b-col>

          <!-- Legal Document -->
          <b-col md="4" cols="12">
            <b-form-group label="Legal Document:">
              <div style="margin-left: 3px; margin-bottom: 15px">
                <div v-if="stakeholder.legal_document">
                  <!-- Image preview -->
                  <div v-if="isImage(stakeholder.legal_document)">
                    <img
                      :src="fullUrl(stakeholder.legal_document)"
                      alt="Legal Document"
                      width="100"
                      height="100"
                      style="cursor: pointer"
                      @click="openLightbox(fullUrl(stakeholder.legal_document))"
                    />
                  </div>

                  <!-- Other files -->
                  <div v-else>
                    <i :class="fileIcon(stakeholder.legal_document)" style="margin-right: 5px"></i>
                    <b-button
                      @click="handleDocument(stakeholder.legal_document)"
                      variant="primary"
                      class="btn-sm"
                    >
                      Download Document
                    </b-button>
                  </div>
                </div>
                <div v-else>
                  <span>No document available</span>
                </div>
              </div>
            </b-form-group>
          </b-col>

          <!-- Notes -->
          <b-col md="12" cols="12">
            <b-form-group label="Notes:">
              <b-form-textarea v-model="stakeholder.notes" rows="3" disabled />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <!-- Finalized Profits -->
      <b-card>
        <div style="background-color: rgb(97, 116, 152); height: 32px; border-radius: 4px;">
          <h4 style="color: rgb(223, 227, 238); margin-left: 5px; font-weight: bold;">
            Finalized Profit Reports
          </h4>
        </div>

        <!-- Show profits loading state if stakeholder is loaded but profits are still loading -->
        <div v-if="profitsLoading" class="text-center py-4">
          <b-spinner variant="secondary" label="Loading profits..."></b-spinner>
          <p class="mt-2">Loading profit reports...</p>
        </div>

        <!-- Show message if no profits -->
        <div v-else-if="!profits.length" class="text-center py-4">
          <p class="text-muted">No profit reports available for this stakeholder.</p>
        </div>

        <!-- Profits table -->
        <b-table v-else :items="profits" :fields="fields" striped bordered responsive>
          <!-- Format Month -->
          <template #cell(month_year)="row">
            <div class="text-center font-weight-bold">
              {{ row.item.month_year }}
            </div>
          </template>

          <!-- Format Earnings -->
          <template #cell(total_earnings)="row">
            <div class="text-center font-weight-bold">
              {{ formatCurrency(row.item.total_earnings) }}
            </div>
          </template>

          <!-- Format Expenses -->
          <template #cell(total_expenses)="row">
            <div class="text-center font-weight-bold">
              {{ formatCurrency(row.item.total_expenses) }}
            </div>
          </template>

          <!-- Format Profit -->
          <template #cell(total_profit)="row">
            <div class="text-center font-weight-bold text-primary">
              {{ formatCurrency(row.item.total_profit) }}
            </div>
          </template>

          <!-- Format Share % -->
          <template #cell(stakeholder_share)="row">
            <div class="text-center font-weight-bold">
              {{ row.item.stakeholder_share }}%
            </div>
          </template>

          <!-- Format Stakeholder Profit -->
          <template #cell(stakeholder_profit)="row">
            <div class="text-center font-weight-bold text-light bg-dark">
              {{ formatCurrency(row.item.stakeholder_profit) }}
            </div>
          </template>

          <!-- Actions column -->
          <template #cell(actions)="row">
            <b-button
              @click="downloadProfitReport(row.item.id)"
              variant="link"
              class="p-0"
            >
              <i class="fas fa-file-pdf" style="color: red; font-size: 18px"></i>
            </b-button>
          </template>

          <!-- Summary row -->
          <template #foot()>
            <tr>
              <th class="text-center font-weight-bold">Total</th>
              <th class="text-center font-weight-bold">
                {{ formatCurrency(totalEarnings) }}
              </th>
              <th class="text-center font-weight-bold">
                {{ formatCurrency(totalExpenses) }}
              </th>
              <th class="text-center font-weight-bold text-primary">
                {{ formatCurrency(totalNetProfit) }}
              </th>
              <th></th>
              <th class="text-center font-weight-bold text-light bg-dark">
                {{ formatCurrency(totalShareProfit) }}
              </th>
              <th></th>
            </tr>
          </template>
        </b-table>
      </b-card>
    </div>

    <!-- Lightbox -->
    <div v-if="lightboxVisible" class="lightbox" @click.self="closeLightbox">
      <div class="lightbox-content">
        <img :src="selectedImage" alt="Preview" class="lightbox-image" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BTable,
  BSpinner,
  BAlert,
} from "bootstrap-vue";
import axios from "../../../axios";

export default {
  data() {
    return {
      stakeholder: {},
      profits: [],
      isLoading: true,
      profitsLoading: false,
      error: null,
      fields: [
        { key: "month_year", label: "Month-Year" },
        { key: "total_earnings", label: "Total Earnings" },
        { key: "total_expenses", label: "Total Expenses" },
        { key: "total_profit", label: "Net Profit" },
        { key: "stakeholder_share", label: "Share (%)" },
        { key: "stakeholder_profit", label: "Stakeholder Profit" },
        { key: "actions", label: "Actions" },
      ],
      lightboxVisible: false,
      selectedImage: null,
    };
  },
  computed: {
    totalEarnings() {
      return this.profits.reduce((sum, p) => sum + parseFloat(p.total_earnings || 0), 0);
    },
    totalExpenses() {
      return this.profits.reduce((sum, p) => sum + parseFloat(p.total_expenses || 0), 0);
    },
    totalNetProfit() {
      return this.profits.reduce((sum, p) => sum + parseFloat(p.total_profit || 0), 0);
    },
    totalShareProfit() {
      return this.profits.reduce((sum, p) => sum + parseFloat(p.stakeholder_profit || 0), 0);
    },
  },
  components: {
    BRow,
    BCol,
    BCard,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BTable,
    BSpinner,
    BAlert,
  },
  created() {
    this.fetchStakeholder();
  },
  methods: {
    async fetchStakeholder() {
      try {
        this.isLoading = true;
        this.error = null;
        
        const response = await axios.get(`stakeholders/${this.$route.params.id}`);
        this.stakeholder = response.data.data;
        this.profits = this.stakeholder.profits || [];
        
        this.isLoading = false;
      } catch (error) {
        console.error("Error fetching stakeholder:", error);
        this.error = "Failed to load stakeholder information. Please try again.";
        this.isLoading = false;
      }
    },
    fullUrl(path) {
      return "https://wossdb.wossautoid.com/" + path;
    },
    openLightbox(imgPath) {
      this.selectedImage = imgPath;
      this.lightboxVisible = true;
    },
    closeLightbox() {
      this.lightboxVisible = false;
    },
    isImage(fileName) {
      return /\.(jpg|jpeg|png|gif)$/i.test(fileName);
    },
    fileIcon(fileName) {
      if (/\.pdf$/i.test(fileName)) return "fas fa-file-pdf";
      if (/\.(csv|xls|xlsx)$/i.test(fileName)) return "fas fa-file-excel";
      if (/\.(doc|docx)$/i.test(fileName)) return "fas fa-file-word";
      return "fas fa-file-alt";
    },
    downloadFile(url) {
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", url.split("/").pop());
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    handleDocument(fileUrl) {
      const fullFilePath = this.fullUrl(fileUrl);
      if (this.isImage(fileUrl)) {
        this.openLightbox(fullFilePath);
      } else if (/\.pdf$/i.test(fileUrl)) {
        window.open(fullFilePath, "_blank");
      } else {
        this.downloadFile(fullFilePath);
      }
    },
    downloadProfitReport(profitId) {
      this.$router.push({ name: "ProfitPdf", params: { id: profitId } });
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(value || 0);
    },
  },
};
</script>

<style scoped>
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.lightbox-content {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  max-width: 90%;
  max-height: 90%;
  overflow: auto;
}
.lightbox-image {
  max-width: 100%;
  max-height: 80vh;
}
</style>